import { STATUS_CODE } from "@/constants/errorCode";
import { getErrorMessage } from "@/helpers/handleErrorResponse";
import { removeEmptyParams } from "@/helpers/objectHelpers";
import {
  AnalysisSummaryParamsPayload,
  GetAnalyticDetailResponse,
  SaveAnalysisUserDatasetPayload,
  SaveAnalysisUserDatasetV2Response
} from "@/types/analysis";
import { ExportDatasetFileType } from "@/types/dataset";
import {
  AnalysisDatasetLegendConfig,
  AnalysisLegendConfigPayload,
  GetAnalysisLegendConfigResponse
} from "@/types/project";
import {
  GetAnalysisTemplateDetail,
  GetAnalysisTemplatePayload,
  GetAnalysisTemplateResponse
} from "@/types/template";
import { CustomError } from "@/utils/errors";

import qs from "qs";

import axiosInstance from "../config";

export const saveAnalysisOutputAsDataset = async (
  payload: SaveAnalysisUserDatasetPayload
) => {
  try {
    const { analysisId, datasets } = payload;
    const { data } = await axiosInstance.post(
      `/v1/analysis/${analysisId}/save_dataset`,
      { datasets }
    );
    return data;
  } catch (error) {
    const { status, message, details } = getErrorMessage(error);
    throw new CustomError(message, status, details);
  }
};

/** Async counterpart of `saveAnalysisOutputAsDataset`: returns an operation id
 * whose status must be polled through `getOperationStatus`. */
export const saveAnalysisOutputAsDatasetV2 = async (
  payload: SaveAnalysisUserDatasetPayload
): Promise<SaveAnalysisUserDatasetV2Response> => {
  try {
    const { analysisId, datasets } = payload;
    const { data } = await axiosInstance.post(
      `/v2/analysis/${analysisId}/save_dataset`,
      { datasets }
    );
    return data;
  } catch (error) {
    const { status, message, details } = getErrorMessage(error);
    throw new CustomError(message, status, details);
  }
};

export const getAnalysisSummary = async (datasetId: string) => {
  try {
    const { data } = await axiosInstance.get(
      `/v1/analysis/${datasetId}/summary`
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getAnalysisStatus = async (analysisId: string) => {
  try {
    const { data } = await axiosInstance.get(
      `/v1/analysis/${analysisId}/status`
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getAnalysis = async (analysisId: string) => {
  try {
    const { data } = await axiosInstance.get(`/v2/analysis/${analysisId}`);
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getAnalysisDatasetsV2 = async (
  analysisId: string,
  datasetIds: string[] | string
): Promise<GetAnalyticDetailResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `/v2/analysis/${analysisId}/datasets`,
      { params: { ids: datasetIds } }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getSchemaDatasetByAnalysis = async (
  analysisId: string | undefined,
  datasetId: string
) => {
  try {
    const { data } = await axiosInstance.get(
      `/v1/analysis/${analysisId}/datasets/${datasetId}`
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getAnalysisSummaryByDataset = async (
  payload: AnalysisSummaryParamsPayload
) => {
  const { datasetId, analysisId, ...rest } = payload;
  try {
    const { data } = await axiosInstance.get(
      `/v2/analysis/${analysisId}/datasets/${datasetId}/summary`,
      {
        params: removeEmptyParams(rest),
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" })
      }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const getAnalysisTemplate = async (
  payload: GetAnalysisTemplatePayload
): Promise<GetAnalysisTemplateResponse> => {
  try {
    const { data } = await axiosInstance.get("/v1/analysis-templates", {
      params: payload
    });

    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    throw new CustomError(message, status);
  }
};

export const getAnalysisTemplateDetail = async (
  templateId: string
): Promise<GetAnalysisTemplateDetail> => {
  try {
    const { data } = await axiosInstance.get(
      `/v1/analysis-template/${templateId}`
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    throw new CustomError(message, status);
  }
};

export const exportAnalysisSummary = async (
  analysisId: string,
  fileType: ExportDatasetFileType
) => {
  const { data } = await axiosInstance.get(
    `/v1/analysis/${analysisId}/export`,
    {
      params: { file_type: fileType }
    }
  );
  return data;
};

/** Saves the post-analysis legend config: `{ datasets: [...] }`, always the full
 * replacement list. */
export const putAnalysisLegendConfig = async (
  analysisId: string,
  payload: AnalysisLegendConfigPayload
) => {
  try {
    const { data } = await axiosInstance.put(
      `/v1/analysis/${analysisId}/legend-config`,
      payload
    );
    return data;
  } catch (error) {
    const { status, message, details } = getErrorMessage(error);
    throw new CustomError(message, status, details);
  }
};

/**
 * Reads the post-analysis legend config out of the BFF's `{ data: { datasets } }`
 * response. `null` when nothing has been saved yet (empty `data`, or 404 on an
 * analysis that predates the column), so the caller can fall back.
 */
export const getAnalysisLegendConfig = async (
  analysisId: string
): Promise<AnalysisDatasetLegendConfig[] | null> => {
  try {
    const { data } = await axiosInstance.get<GetAnalysisLegendConfigResponse>(
      `/v1/analysis/${analysisId}/legend-config`
    );
    return data?.data?.datasets ?? null;
  } catch (error) {
    const { status, message, details } = getErrorMessage(error);
    if (status === STATUS_CODE.NOT_FOUND) return null;
    throw new CustomError(message, status, details);
  }
};

export const cancelAnalysisJob = async (analysisId: string) => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/analysis/${analysisId}/cancel`
    );
    return data;
  } catch (error) {
    const { status, message, details } = getErrorMessage(error);
    throw new CustomError(message, status, details);
  }
};

export const rerunAnalysisJob = async (analysisId: string) => {
  try {
    const { data } = await axiosInstance.post(
      `/v1/analysis/${analysisId}/rerun`
    );
    return data;
  } catch (error) {
    const { status, message, details } = getErrorMessage(error);
    throw new CustomError(message, status, details);
  }
};
